"use client";
import type {
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";
import { OfflineImage } from "../../shared/offline-image";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { Input } from "../../ui/input.jsx";
import { Label } from "../../ui/label.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Textarea } from "../../ui/textarea";

import {
  AlertCircle,
  Download,
  Eye,
  FileText,
  Loader2,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import {
  useObservationsWithKeyIssues,
  useVersionAwareFiles,
} from "../../../hooks/use-version-aware-data";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  cloneFiles,
  deleteFile,
  fetchFiles,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import {
  createObservation,
  fetchObservationsBySiteId,
} from "../../../redux/slices/observationSlice.js";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "../shared/image-capture-picker";
import { LocalSectionImportControl } from "../shared/local-section-import-control";

import {
  createObservationWithKeyIssues,
  saveLinkedKeyIssue,
} from "../../../redux/slices/keyIssueMatrixSlice.js";

import { useNotification } from "../../../hooks/useNotification";
import { notificationHelpers } from "../../../utils/notificationHelpers";

// Used to match an imported key issue against an observation already on
// this report so key issues are appended to it instead of duplicating it.
const normalizeTitleKey = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeRiskKey = (risk: any) =>
  [risk?.title, risk?.description, risk?.comments]
    .map((value) =>
      String(value || "")
        .trim()
        .toLowerCase(),
    )
    .join("|");

const getSavedRiskIdForFile = (
  file: any,
  stagedRisks: any[] = [],
  savedRisks: any[] = [],
) => {
  const stagedRiskIndex = stagedRisks.findIndex(
    (risk: any) =>
      String(risk?.id || risk?._id) === String(file?.relatedTo?.entityId),
  );
  const stagedRisk = stagedRisks[stagedRiskIndex];
  const savedRisk =
    savedRisks.find(
      (risk: any) => normalizeRiskKey(risk) === normalizeRiskKey(stagedRisk),
    ) || (stagedRiskIndex >= 0 ? savedRisks[stagedRiskIndex] : undefined);
  const savedRiskId = String(savedRisk?._id || "");
  return /^[a-f\d]{24}$/i.test(savedRiskId) ? savedRiskId : undefined;
};

// ========== INTERFACES ==========
interface FileAttachment extends AuditFileMetadata {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  uploadedDate: string;
  description?: string;
}

interface Risk {
  _id?: string;
  id?: string;
  title: string;
  description: string;
  severity: number;
  status: "open" | "in-progress" | "resolved" | "closed";
  assignee: string;
  dueDate: string;
  riskLevel: "low" | "medium" | "high";
  likelihood: number;
  score: number;
  comments?: string;
  recommendation: string;
  owner: string;
  attachments: FileAttachment[];
  observationId?: string; // Link to observation for database persistence
}

interface RiskMatrixData {
  risks: Risk[];
  overallRiskLevel: "low" | "medium" | "high" | "critical";
  notes: string;
}

// ========== CONSTANTS ==========
// Fixed matrix data based on your specifications
const LIKELIHOOD_LEVELS = [
  { level: 1, description: "Unlikely", description2: "No Injury" },
  { level: 2, description: "Possible", description2: "Minor Injury" },
  { level: 3, description: "Even chance", description2: "Over 7 days" },
  { level: 4, description: "Highly likely", description2: "Major Injury" },
  { level: 5, description: "Almost certain", description2: "Death" },
];

const SEVERITY_LEVELS = [
  { level: 1, description: "No Injury" },
  { level: 2, description: "Minor Injury" },
  { level: 3, description: "Over 7 days" },
  { level: 4, description: "Major Injury" },
  { level: 5, description: "Death" },
];

const RISK_CLASSIFICATION = [
  {
    range: "1-3",
    classification: "Low",
    color: "bg-green-600 text-green-800",
    acceptability: "Y - Accepted, no action required",
  },
  {
    range: "4-6",
    classification: "Low - Med",
    color: "bg-green-500 text-blue-800",
    acceptability: "Y or N - Broadly acceptable, mitigate risk if practicable",
  },
  {
    range: "7-10",
    classification: "Med",
    color: "bg-yellow-500 text-yellow-800",
    acceptability: "Y or N - Moderate - Reduce risk if reasonably practicable",
  },
  {
    range: "11-15",
    classification: "High",
    color: "bg-red-500 text-orange-800",
    acceptability:
      "N - High Risk - Prior action to be undertaken to reduce/mitigate risk",
  },
  {
    range: "16-25",
    classification: "High",
    color: "bg-red-500 text-red-800",
    acceptability:
      "N - Unacceptable - action must be taken immediately. Stop work",
  },
];

// Add this constant:
const acceptabilityColors = {
  accepted: "bg-green-100 text-green-800",
  "broadly-acceptable": "bg-blue-100 text-blue-800",
  moderate: "bg-yellow-100 text-yellow-800",
  "high-risk": "bg-orange-100 text-orange-800",
  unacceptable: "bg-red-100 text-red-800",
};

const initialData: RiskMatrixData = {
  risks: [],
  overallRiskLevel: "medium",
  notes: "",
};

const riskLevelColors = {
  low: "bg-green-500 text-green-800",
  medium: "bg-yellow-500 text-yellow-800",
  high: "bg-red-500 text-red-800",
};

const riskLevelOptions = [
  { value: "low", label: "Low Risk" },
  { value: "medium", label: "Medium Risk" },
  { value: "high", label: "High Risk" },
];

const getAcceptabilityText = (score: number) => {
  if (score <= 3) return "Accepted, no action required";
  if (score <= 6) return "Broadly acceptable, mitigate risk if practicable";
  if (score <= 10) return "Moderate - Reduce risk if reasonably practicable";
  if (score <= 15)
    return "High Risk - Prior action to be undertaken to reduce/mitigate risk";
  return "Unacceptable - action must be taken immediately. Stop work";
};

// ========== PROGRESS CALCULATION ==========
const calculateProgress = (risks, files) => {
  try {
    if (risks?.length === 0) return 0;
    const filledRisks =
      risks?.filter(
        (risk) =>
          risk?.title?.trim() &&
          risk?.description?.trim() &&
          files?.some((file) => file?.relatedTo?.entityId === risk?._id),
      )?.length ?? 0;
    return Math.round((filledRisks / risks?.length) * 100);
  } catch (error) {
    console.error("Error calculating progress:", error);
    return 0;
  }
};
// ========== COMPONENT ==========
export const CombinedRiskMatrix = memo(function CombinedRiskMatrix({
  siteId,
  templateId,
  reportId,
  sessionProgress,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: {
  siteId: string;
  templateId: string;
  reportId?: string;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: unknown;
  previousCompletedReports?: any[];
}) {
  const { notify } = useNotification();

  const [formData] = useState<RiskMatrixData>(initialData);
  const [activeTab, setActiveTab] = useState<
    "matrix" | "risks" | "classification"
  >("risks");
  const [selectedFile, setSelectedFile] = useState<FileAttachment | null>(null);

  const [riskDrafts, setRiskDrafts] = useState<
    Record<string, { observationId: string; riskData: Risk }>
  >({});
  const [uploadingRiskImages, setUploadingRiskImages] = useState<
    Record<string, boolean>
  >({});
  const [punchlistProgressData, setPunchlistProgressData] = useState([]);
  const [pendingRiskMatrixImports, setPendingRiskMatrixImports] = useState<
    any[]
  >([]);
  const resetRiskSaveBaselineRef = useRef<(data: any) => void>(() => undefined);

  // Wizard state for custom risk creation
  const [showRiskWizard, setShowRiskWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState<
    "select-observation" | "add-risk"
  >("select-observation");
  const [wizardObservationId, setWizardObservationId] = useState<string>("");
  const [wizardObservationTitle, setWizardObservationTitle] =
    useState<string>("");
  const createNewObservationValue = "__create_new__";
  const [wizardRiskData, setWizardRiskData] = useState<Partial<Risk>>({
    title: "",
    description: "",
    severity: 1,
    status: "open",
    assignee: "",
    dueDate: "",
    riskLevel: "medium",
    likelihood: 1,
    score: 1,
    recommendation: "",
    owner: "",
    attachments: [],
  });

  // const { files = [] } = useAppSelector((state) => state?.files);
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  const loadingRiskMatrix = useAppSelector(
    (state) => state?.keyIssueMatrix?.loading,
  );
  const dispatch = useAppDispatch();

  // Load version data when viewing historical version
  const currentObservationData = useObservationsWithKeyIssues(readOnly);

  const progress = calculateProgress(punchlistProgressData, files);
  useEffect(() => {
    setSessionProgress((previous) => {
      if (previous?.[templateId]?.["key-issue-matrix"] === progress)
        return previous;
      return {
        ...previous,
        [templateId]: {
          ...previous?.[templateId],
          "key-issue-matrix": progress,
        },
      };
    });
  }, [progress, templateId, setSessionProgress]);
  // ========== INITIALIZATION ==========
  useEffect(() => {
    // Fetch observations from this site for selection dropdown
    dispatch(fetchObservationsBySiteId(siteId));
  }, [siteId, dispatch]);

  useEffect(() => {
    dispatch(fetchFiles({ siteId, reportId }) as any);
  }, [dispatch, siteId, reportId]);

  // General observations (the ones the key-issue-matrix section reads/writes)
  // used both to derive punchlistProgressData above and to de-duplicate
  // imports against observations already imported from the same source.
  const generalObservationData = useMemo(
    () =>
      (currentObservationData || []).filter(
        (obs: any) => obs?.observationType === "general",
      ),
    [currentObservationData],
  );

  const displayedObservationData = useMemo(() => {
    const observations = [...generalObservationData];
    pendingRiskMatrixImports.forEach((pendingObservation) => {
      const existingIndex = observations.findIndex(
        (observation) =>
          observation._id === pendingObservation._targetObservationId,
      );
      if (existingIndex >= 0) {
        observations[existingIndex] = {
          ...observations[existingIndex],
          keyIssues: pendingObservation.keyIssues,
        };
      } else {
        observations.push(pendingObservation);
      }
    });
    return observations;
  }, [generalObservationData, pendingRiskMatrixImports]);

  useEffect(() => {
    const punchlist = displayedObservationData.flatMap(
      (observation) => observation.keyIssues || [],
    );
    setPunchlistProgressData(punchlist);
  }, [displayedObservationData]);

  const handleImportRiskMatrixObservations = useCallback(
    (nextObservations: any[]) => {
      const newlyImported = (nextObservations || []).filter(
        (obs: any) => obs?._sourceId,
      );

      if (newlyImported.length === 0) return;

      setPendingRiskMatrixImports((previous) => {
        const next = [...previous];
        newlyImported.forEach((obs: any) => {
          const riskItems = (obs?.keyIssues || []).map(
            ({ _id, ...risk }: any) => risk,
          );
          if (riskItems.length === 0) return;

          const importedTitleKey = normalizeTitleKey(obs?.title);
          const matchingObservation =
            generalObservationData.find(
              (existing: any) =>
                normalizeTitleKey(existing?.title) === importedTitleKey,
            ) ||
            next.find(
              (existing: any) =>
                normalizeTitleKey(existing?.title) === importedTitleKey,
            );
          const targetId = matchingObservation?._id;
          const targetIndex = next.findIndex(
            (existing) =>
              (targetId && existing._targetObservationId === targetId) ||
              existing._sourceId === obs._sourceId,
          );
          const existingRiskItems =
            targetIndex >= 0
              ? next[targetIndex].keyIssues || []
              : matchingObservation?.keyIssues || [];
          const riskKeys = new Set(existingRiskItems.map(normalizeRiskKey));
          const newRiskItems = riskItems.filter((risk: any) => {
            const key = normalizeRiskKey(risk);
            if (riskKeys.has(key)) return false;
            riskKeys.add(key);
            return true;
          });
          if (newRiskItems.length === 0) return;

          const stagedObservation = {
            ...obs,
            keyIssues: [...existingRiskItems, ...newRiskItems],
            _newRiskItems: newRiskItems,
            ...(targetId ? { _targetObservationId: targetId } : {}),
          };
          if (targetIndex >= 0) next[targetIndex] = stagedObservation;
          else next.push(stagedObservation);
        });
        return next;
      });
      notify("Key issue matrix imported. Click Save to persist it.", "success");
    },
    [notify, generalObservationData],
  );

  // ========== SAVE HANDLING ==========
  const handleSave = useCallback(
    async ({
      drafts,
      pendingImports,
    }: {
      drafts: typeof riskDrafts;
      pendingImports: any[];
    }) => {
      const mutationResults: unknown[] = [];
      const trackMutation = async <T,>(request: Promise<T>): Promise<T> => {
        const result = await request;
        mutationResults.push(result);
        return result;
      };
      try {
        // These updates must be sequential. Concurrent saves targeting risks in
        // the same observation can overwrite each other's document changes.
        for (const [riskId, draft] of Object.entries(drafts)) {
          await trackMutation(
            dispatch(
              saveLinkedKeyIssue({
                observationId: draft.observationId,
                riskId,
                updateType: "risk",
                action: "update",
                riskData: draft.riskData,
              }),
            ).unwrap(),
          );
        }
        for (const importedObservation of pendingImports) {
          if (importedObservation._targetObservationId) {
            let savedObservation: any = null;
            for (const riskData of importedObservation._newRiskItems || []) {
              savedObservation = await trackMutation(
                dispatch(
                  saveLinkedKeyIssue({
                    observationId: importedObservation._targetObservationId,
                    updateType: "risk",
                    action: "create",
                    riskData,
                  }),
                ).unwrap(),
              );
            }
            if (importedObservation._pendingImportedFiles?.length > 0) {
              const filesToClone = importedObservation._pendingImportedFiles
                .map((file: any) => {
                  const riskId = getSavedRiskIdForFile(
                    file,
                    importedObservation.keyIssues,
                    savedObservation?.keyIssues,
                  );
                  if (!riskId) return null;
                  return {
                    ...file,
                    relatedTo: {
                      ...(file.relatedTo || {}),
                      entityType: "key-issue-matrix",
                      entityId: riskId,
                    },
                  };
                })
                .filter(Boolean);
              if (filesToClone.length > 0) {
                await trackMutation(
                  dispatch(
                    cloneFiles({ files: filesToClone, siteId, reportId }),
                  ).unwrap(),
                );
              }
            }
          } else {
            const {
              id,
              _id,
              _importedLocalId,
              _sourceId,
              _pendingImportedFiles,
              _targetObservationId,
              _newRiskItems,
              ...observation
            } = importedObservation;
            const savedObservation = await trackMutation(
              dispatch(
                createObservation({
                  ...observation,
                  siteId,
                  reportId,
                  templateId,
                  importedFrom: importedObservation._sourceId,
                }),
              ).unwrap(),
            );
            const filesToClone = (_pendingImportedFiles || [])
              .map((file: any) => {
                const riskId = getSavedRiskIdForFile(
                  file,
                  observation.keyIssues,
                  savedObservation?.keyIssues,
                );
                if (!riskId) return null;
                return {
                  ...file,
                  relatedTo: {
                    ...(file.relatedTo || {}),
                    entityType: "key-issue-matrix",
                    entityId: riskId,
                  },
                };
              })
              .filter(Boolean);
            if (filesToClone.length > 0) {
              await trackMutation(
                dispatch(
                  cloneFiles({ files: filesToClone, siteId, reportId }),
                ).unwrap(),
              );
            }
          }
        }
        if (pendingImports.length > 0) {
          setPendingRiskMatrixImports([]);
          dispatch(fetchObservationsBySiteId(siteId));
        }
        resetRiskSaveBaselineRef.current({ drafts, pendingImports: [] });
        const config = notificationHelpers.formSaveSuccess(
          "Risk Matrix",
          mutationResults,
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Risk Matrix",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, notify, reportId, siteId, templateId],
  );

  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetRiskSaveBaseline,
  } = useDebouncedSave(
    { drafts: riskDrafts, pendingImports: pendingRiskMatrixImports },
    {
      delay: 5000,
      onSave: handleSave,
      onError: (error) => console.error("Save error:", error),
      readOnly,
    },
  );
  resetRiskSaveBaselineRef.current = resetRiskSaveBaseline;

  // Save handler for file descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const { updateFile } =
          await import("../../../redux/slices/fileSlice.js");
        await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            dispatch(updateFile({ fileId, fileData: { fileDescription } })),
          ),
        );
        const config =
          notificationHelpers.formSaveSuccess("Image Descriptions");
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for file descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
  }, [forceFormSave, forceImageSave]);

  // ========== FILE HANDLING ==========
  const handleFileUpload = (
    files: FileList | File[],
    entityType: string,
    entityId?: string,
  ) => {
    if (!entityId) return;

    try {
      const fileArray = Array.from(files);
      setUploadingRiskImages((prev) => ({ ...prev, [entityId]: true }));
      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId: siteId,
              entityType: entityType,
              entityId: entityId,
              reportId,
            },
            filterData: { entityType: true },
          }),
        ),
      )
        .then((mutationOutcome) => {
          // Show success notification for each uploaded file
          fileArray.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          // Show error notification
          const config = notificationHelpers.imageUploadError(
            fileArray.length === 1
              ? fileArray[0].name
              : `${fileArray.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })

        .finally(() => {
          setUploadingRiskImages((prev) => ({ ...prev, [entityId]: false }));
        });
    } catch (error) {
      setUploadingRiskImages((prev) => ({
        ...prev,
        [entityId]: false,
      }));
      console.error("File upload error:", error);
    }
  };

  const removeFile = (fileId: string) => {
    try {
      requireMutationSuccess(dispatch(deleteFile(fileId)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "Image",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "Image",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      console.error("File deletion error:", error);
    }
  };

  // Update file description - updates local state; debounced save handles the dispatch
  const updateImageDescription = useCallback(
    (fileId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [fileId]: description }));
    },
    [setImageDescriptions],
  );

  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const getFilesByEntity = (
    entityType: string,
    entityId?: string,
  ): FileAttachment[] => {
    try {
      const stagedFiles = pendingRiskMatrixImports.flatMap(
        (observation: any) => observation._pendingImportedFiles || [],
      );
      return (
        [...stagedFiles, ...(files || [])].filter(
          (file) =>
            file?.relatedTo?.entityType === entityType &&
            (!entityId || file?.relatedTo?.entityId === entityId),
        ) ?? []
      );
    } catch (error) {
      console.error("Error getting files by entity:", error);
      return [];
    }
  };

  const addRisk = () => {
    setShowRiskWizard(true);
    setWizardStep("select-observation");
    setWizardObservationId("");
    setWizardObservationTitle("");
    setWizardRiskData({
      title: "",
      description: "",
      severity: 1,
      status: "open",
      assignee: "",
      dueDate: "",
      riskLevel: "medium",
      likelihood: 1,
      score: 1,
      recommendation: "",
      owner: "",
      attachments: [],
    });
  };

  const completeRiskWizard = async () => {
    const mutationResults: unknown[] = [];
    const trackMutation = async <T,>(request: Promise<T>): Promise<T> => {
      const result = await request;
      mutationResults.push(result);
      return result;
    };
    if (!wizardRiskData.title) {
      alert("Please enter a risk title");
      return;
    }

    if (!wizardObservationId && !wizardObservationTitle.trim()) {
      alert(
        "Please enter an observation title or select an existing observation",
      );
      return;
    }

    const newRisk: Risk = {
      id: `risk-${Date.now()}`,
      title: wizardRiskData.title || "",
      description: wizardRiskData.description || "",
      severity: wizardRiskData.severity || 1,
      status: wizardRiskData.status || "open",
      assignee: wizardRiskData.assignee || "",
      dueDate: wizardRiskData.dueDate || "",
      riskLevel: wizardRiskData.riskLevel || "medium",
      likelihood: wizardRiskData.likelihood || 1,
      score: wizardRiskData.score || 1,
      recommendation: wizardRiskData.recommendation || "",
      owner: wizardRiskData.owner || "",
      attachments: wizardRiskData.attachments || [],
    };

    try {
      if (wizardObservationId) {
        await trackMutation(
          dispatch(
            saveLinkedKeyIssue({
              observationId: wizardObservationId,
              updateType: "risk",
              action: "create",
              riskData: newRisk,
            }),
          ).unwrap(),
        );
      } else {
        const observationTitle =
          wizardObservationTitle.trim() ||
          wizardRiskData.title ||
          "Risk Matrix Observation";
        await trackMutation(
          dispatch(
            createObservationWithKeyIssues({
              siteId,
              reportId,
              keyIssues: [newRisk],
              observationType: "general",
              title: observationTitle,
              templateId,
            }),
          ).unwrap(),
        );
      }

      setShowRiskWizard(false);
      const config = notificationHelpers.actionCreate(
        "Risk Matrix",
        mutationResults,
      );
      notify(config.message, config.type, config.options);
    } catch (error) {
      const config = notificationHelpers.actionCreateError(
        "Risk Matrix",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
      console.error("Error creating risk:", error);
    }
  };

  const removeRisk = async (id: string, obsId: string) => {
    if (readOnly) return;
    try {
      const mutationResult6 = await dispatch(
        saveLinkedKeyIssue({
          observationId: obsId,
          riskId: id,
          updateType: "risk",
          action: "delete",
        }),
      ).unwrap();
      const config = notificationHelpers.actionDelete("Risk Matrix", [
        mutationResult6,
      ]);
      setRiskDrafts((previous) => {
        const next = { ...previous };
        delete next[id];
        return next;
      });
      notify(config.message, config.type, config.options);
    } catch (error) {
      const config = notificationHelpers.actionDeleteError(
        "Risk Matrix",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
      console.error("Error removing risk:", error);
    }
  };

  const updateRisk = (
    obsId: string | undefined,
    id: string,
    field: keyof Risk,
    value: string | number,
  ) => {
    if (!obsId || !id) return;

    const clampToRange = (inputValue: string | number) => {
      const parsedValue = Number(inputValue);
      if (Number.isNaN(parsedValue)) return 1;
      return Math.min(5, Math.max(1, parsedValue));
    };

    const normalizedValue =
      field === "severity" || field === "likelihood"
        ? clampToRange(value)
        : field === "score"
          ? Number(value)
          : value;

    if (
      (field === "severity" || field === "likelihood" || field === "score") &&
      Number.isNaN(normalizedValue)
    ) {
      return;
    }

    setPunchlistProgressData((prev) =>
      prev.map((risk) =>
        risk.id === id || risk._id === id
          ? {
              ...risk,
              [field]: normalizedValue,
            }
          : risk,
      ),
    );

    const currentRisk = displayedObservationData
      ?.filter((obs) => obs.observationType === "general")
      ?.flatMap((obs) => obs.keyIssues || [])
      .find((item) => item._id === id || item.id === id);

    const existingDraft = riskDrafts[id]?.riskData;
    if (!currentRisk && !existingDraft) return;

    setRiskDrafts((prev) => ({
      ...prev,
      [id]: {
        observationId: obsId,
        riskData: {
          ...(currentRisk as Risk),
          ...prev[id]?.riskData,
          [field]: normalizedValue,
        },
      },
    }));
  };

  const getRiskLevel = (risk: Risk | number) => {
    const score =
      typeof risk === "number"
        ? risk
        : risk.likelihood * risk.severity || risk.score;
    if (score <= 6) return "low";
    else if (score <= 10) return "medium";
    else return "high";
  };

  // ========== TABLE RENDERING ==========
  const renderRiskMatrixTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse border border-slate-200 dark:border-border">
        <thead>
          <tr>
            <th className="border border-slate-200 p-2 font-semibold text-start dark:border-border">
              Likelihood / Severity
            </th>
            {SEVERITY_LEVELS?.map((severity) => (
              <th
                key={severity.level}
                className="border border-slate-200 p-2 font-semibold text-center dark:border-border"
              >
                {severity.level}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {LIKELIHOOD_LEVELS?.map((likelihood) => (
            <tr key={likelihood.level}>
              <td className="border border-slate-200 p-2 font-semibold dark:border-border">
                {likelihood.level}
              </td>
              {SEVERITY_LEVELS?.map((severity) => {
                const score = likelihood.level * severity.level;
                const riskLevel = getRiskLevel(score);
                return (
                  <td
                    key={severity.level}
                    className={`border border-slate-200 p-2 text-center font-semibold ${
                      riskLevelColors[riskLevel]
                    }`}
                  >
                    {score}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderRiskClassificationTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse border border-slate-200 dark:border-border">
        <thead>
          <tr>
            <th className="text-start border border-slate-200 p-2 font-semibold dark:border-border">
              Risk Rating
            </th>
            <th className="text-start border border-slate-200 p-2 font-semibold dark:border-border"></th>
            <th className="text-start border border-slate-200 p-2 font-semibold dark:border-border">
              Acceptability
            </th>
          </tr>
        </thead>
        <tbody>
          {RISK_CLASSIFICATION?.map((item) => (
            <tr key={item.range}>
              <td className="text-start border border-slate-200 p-2 text-center font-semibold dark:border-border">
                {item.range}
              </td>
              <td
                className={`text-start border border-slate-200 p-2 text-center font-semibold ${item.color}`}
              ></td>
              <td className="text-start border border-slate-200 p-2 text-center font-semibold dark:border-border">
                {item.acceptability}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderRiskTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse border border-slate-200 dark:border-border">
        <thead>
          <tr>
            <th className="text-start border border-slate-200 p-2 font-semibold dark:border-border"></th>
            <th className="text-start border border-slate-200 p-2 font-semibold dark:border-border">
              Severity
            </th>
            <th className="text-start border border-slate-200 p-2 font-semibold dark:border-border"></th>
            <th className="text-start border border-slate-200 p-2 font-semibold dark:border-border">
              Likelihood
            </th>
          </tr>
        </thead>
        <tbody>
          {LIKELIHOOD_LEVELS?.map((severity) => (
            <tr key={severity.level}>
              <td className="text-start border border-slate-200 p-2 text-center font-semibold dark:border-border">
                {severity.level}
              </td>
              <td className="text-start border border-slate-200 p-2 text-center font-semibold dark:border-border">
                {severity.description}
              </td>
              <td className="text-start border border-slate-200 p-2 text-center font-semibold dark:border-border">
                {severity.level}
              </td>
              <td className="text-start border border-slate-200 p-2 text-center font-semibold dark:border-border">
                {severity.description2}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ========== RISK CARD COMPONENT ==========
  const renderRiskCard = (
    risk: Risk,
    index: number,
    obsId: string | undefined,
    obsTitle: string | undefined,
  ) => {
    const riskKey = risk._id || risk.id || `risk-${index}`;
    const displayedRisk = { ...risk, ...riskDrafts[riskKey]?.riskData };
    const riskFiles = getFilesByEntity("key-issue-matrix", riskKey);
    const isUploadingRiskImage = uploadingRiskImages[riskKey] || false;
    return (
      <Card key={riskKey} className="p-6">
        <div className="space-y-6">
          {/* HEADER */}
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={riskLevelColors[displayedRisk.riskLevel]}>
                  {displayedRisk.riskLevel?.toUpperCase()} RISK
                </Badge>
                <Badge variant="outline">Score: {displayedRisk.score}</Badge>
                <Badge className={acceptabilityColors[displayedRisk.severity]}>
                  {getAcceptabilityText(displayedRisk.score)}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* RISK TITLE */}
                <OptimizedInput
                  label="Risk Title"
                  id={`title-${risk._id || risk.id}`}
                  value={displayedRisk.title || ""}
                  readOnly={readOnly}
                  onChange={(value) =>
                    updateRisk(obsId, risk._id || risk.id, "title", value)
                  }
                  placeholder="Enter risk title..."
                  required
                />

                {/* RISK LEVEL DROPDOWN - ADDED FIELD */}
                <OptimizedSelect
                  label="Risk Level"
                  id={`risk-level-${risk._id || risk.id}`}
                  value={
                    getRiskLevel(displayedRisk) || displayedRisk.riskLevel || ""
                  }
                  readOnly={readOnly}
                  // onChange={(value) =>
                  //   updateRisk(obsId, risk._id || risk.id, "riskLevel", value)
                  // }
                  placeholder="Select risk level"
                  options={riskLevelOptions}
                  required
                />
              </div>
            </div>
            {!readOnly && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeRisk(risk._id || risk.id, obsId || "")}
                className="text-red-600 hover:text-red-700"
                title="Remove risk"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              label="Severity"
              id={`severity-${risk._id || risk.id}`}
              type="number"
              min={1}
              max={5}
              value={displayedRisk.severity ?? 1}
              onChange={(value) =>
                updateRisk(obsId, risk._id || risk.id, "severity", value)
              }
              readOnly={readOnly}
            />

            <OptimizedInput
              label="Likelihood"
              id={`likelihood-${risk._id || risk.id}`}
              type="number"
              min={1}
              max={5}
              value={displayedRisk.likelihood ?? 1}
              onChange={(value) =>
                updateRisk(obsId, risk._id || risk.id, "likelihood", value)
              }
              readOnly={readOnly}
            />
          </div>

          {/* SCORE FIELD */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              label="Risk Score"
              id={`score-${risk._id || risk.id}`}
              value={
                displayedRisk.severity && displayedRisk.likelihood
                  ? displayedRisk.severity * displayedRisk.likelihood
                  : 0
              }
              readOnly
            />

            <OptimizedInput
              label="Linked Observation"
              id={`observation-${riskKey}`}
              value={obsTitle ? obsTitle : "Untitled Observation"}
              readOnly
              required
            />
          </div>

          <div className="grid grid-cols-1">
            <OptimizedTextarea
              label="Risk Description"
              id={`description-${risk._id || risk.id}`}
              value={displayedRisk.description || ""}
              readOnly={readOnly}
              onChange={(value) =>
                updateRisk(obsId, risk._id || risk.id, "description", value)
              }
              placeholder="Enter risk description..."
              required
            />
          </div>

          <div className="grid grid-cols-1">
            <OptimizedTextarea
              label="Risk Comments"
              id={`comments-${risk._id || risk.id}`}
              value={displayedRisk.comments || ""}
              readOnly={readOnly}
              onChange={(value) =>
                updateRisk(obsId, risk._id || risk.id, "comments", value)
              }
              placeholder="Enter risk comments..."
              required
            />
          </div>

          {/* RISK SUMMARY BOX */}
          <div className="bg-slate-50 p-4 rounded-lg dark:bg-muted">
            <h5 className="font-medium text-slate-900 mb-2 dark:text-foreground">Risk Assessment</h5>
            <p className="text-slate-700 text-sm dark:text-foreground">
              <strong>Classification:</strong>{" "}
              {displayedRisk.riskLevel?.toUpperCase()} | <strong>Score:</strong>{" "}
              {displayedRisk.score} | <strong>Acceptability:</strong>{" "}
              {getAcceptabilityText(displayedRisk.score)}
            </p>
          </div>

          {/* ATTACHMENTS */}
          <div className="space-y-3">
            <Label className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Supporting Documents <span className="text-red-500">*</span>
              <span className="text-xs text-slate-500 ml-1 dark:text-muted-foreground">
                ({riskFiles.length} files)
              </span>
            </Label>

            <div className="space-y-3">
              {isUploadingRiskImage && (
                <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 py-6 text-sm text-slate-600 dark:bg-muted dark:text-muted-foreground dark:border-border">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Uploading images...
                </div>
              )}

              {/* File Grid */}
              {riskFiles.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {riskFiles.map((file) => (
                    <div key={file.id} className="relative group">
                      <div className="aspect-video bg-slate-100 rounded overflow-hidden dark:bg-muted">
                        <OfflineImage
                          fileId={file?._id || file?.id || file?._clientId}
                          src={file.fileUrl || "/placeholder.svg"}
                          alt={file.fileName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute top-1 right-1 flex gap-1">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                          onClick={() => setSelectedFile(file)}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        {!readOnly && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                            onClick={() => removeFile(file.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate truncate">
                            {file.originalName}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {formatFileSize(file.fileSize)}
                          </Badge>
                        </div>
                        <OptimizedTextarea
                          id={`file-desc-${file.id}`}
                          label=""
                          value={
                            imageDescriptions[file.id] ??
                            file?.fileDescription ??
                            ""
                          }
                          readOnly={readOnly}
                          onChange={(value) =>
                            updateImageDescription(file.id, value)
                          }
                          placeholder="Describe this attachment..."
                          rows={1}
                        />
                        <div className="text-xs text-slate-500 dark:text-muted-foreground">
                          Date: {new Date(file.uploadDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}

              {!readOnly && (
                <ImageCapturePicker
                  buttonText="Add Images"
                  isUploading={isUploadingRiskImage}
                  disabled={false}
                  onConfirm={(selectedFiles) =>
                    handleFileUpload(
                      selectedFiles,
                      "key-issue-matrix",
                      risk._id || risk.id,
                    )
                  }
                />
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  // ========== UI UTILITIES ==========
  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  const riskStats = {
    total: formData?.risks?.length ?? 0,
    low: formData?.risks?.filter((r) => r?.riskLevel === "low")?.length ?? 0,
    medium:
      formData?.risks?.filter((r) => r?.riskLevel === "medium")?.length ?? 0,
    high: formData?.risks?.filter((r) => r?.riskLevel === "high")?.length ?? 0,
  };

  const existingObservationOptions = useMemo(() => {
    const generalObservations =
      displayedObservationData?.filter(
        (obs) => obs.observationType === "general",
      ) || [];

    return [
      { value: createNewObservationValue, label: "Create new observation" },
      ...generalObservations.map((obs) => ({
        value: obs._id,
        label:
          obs?.title ||
          `Observation - ${new Date(obs.createdAt).toLocaleDateString()}`,
      })),
    ];
  }, [displayedObservationData, createNewObservationValue]);

  const shouldShowObservationTitle = !wizardObservationId;

  const canProceedToRiskDetails =
    Boolean(
      wizardObservationId && wizardObservationId !== createNewObservationValue,
    ) || Boolean(wizardObservationTitle.trim());

  // ========== RENDER ==========
  return (
    <div className="space-y-4 md:space-y-6">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="key-issue-matrix"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          observationProgressData={generalObservationData}
          setObservationProgressData={(nextObservations: any) =>
            handleImportRiskMatrixObservations(nextObservations)
          }
        />
      )}
      {/* Floating save section */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Risk Legend and Add Button */}
      {/* <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span>High risk</span>
            <div className="bg-red-500 h-4 w-4 rounded"></div>
          </div>
          <div className="flex items-center gap-2">
            <span>Medium risk</span>
            <div className="bg-yellow-400 h-4 w-4 rounded"></div>
          </div>
          <div className="flex items-center gap-2">
            <span>Low risk</span>
            <div className="bg-green-500 h-4 w-4 rounded"></div>
          </div>
        </div>
        <Button onClick={addRisk} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Risk
        </Button>
      </div> */}

      {/* Information Card */}
      {/* <Card>
        <CardContent>
          The risk matrix is a combination of the likelihood of an identified
          hazard occurring multiplied by the severity of such hazard on the
          project. The score ranges from 1 – 5, for both the likelihood of
          occurrence and the severity, the classification of the risk is given
          in Table 2.
        </CardContent>
      </Card> */}

      {/* Main Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as any)}
      >
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="risks">Risk Assessment</TabsTrigger>
          <TabsTrigger value="matrix">Risk Matrix</TabsTrigger>
          <TabsTrigger value="classification">Classification</TabsTrigger>
        </TabsList>

        {/* Risk Assessment Tab */}
        <TabsContent value="risks" className="mt-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Risk Assessment</CardTitle>
                {!readOnly && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addRisk}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Risk
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Custom Risks List */}
              {displayedObservationData
                ?.filter((obs) => obs.observationType === "general")
                .flatMap((obs) => obs.keyIssues)?.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 dark:text-muted-foreground">No risks assessed yet</p>
                  <p className="text-sm text-slate-500 dark:text-muted-foreground">
                    Add risks from the common list or create custom risks
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {displayedObservationData
                    ?.filter((obs) => obs.observationType === "general")
                    ?.flatMap((obs) =>
                      (obs.keyIssues || []).map((risk, index) =>
                        renderRiskCard(risk, index, obs._id, obs.title || ""),
                      ),
                    )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risk Matrix Tab */}
        <TabsContent value="matrix" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Table 1; Risk Matrix Classification</CardTitle>
            </CardHeader>
            <CardContent>{renderRiskTable()}</CardContent>

            <CardHeader>
              <CardTitle>Risk Classification Table</CardTitle>
            </CardHeader>
            <CardContent>{renderRiskClassificationTable()}</CardContent>

            <CardHeader>
              <CardTitle>Risk Matrix Table</CardTitle>
            </CardHeader>
            <CardContent>{renderRiskMatrixTable()}</CardContent>
          </Card>
        </TabsContent>

        {/* Classification Tab */}
        <TabsContent value="classification" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Risk Classification & Acceptability</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {RISK_CLASSIFICATION?.map((item, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-center gap-4">
                      <Badge className={item.color}>
                        {item.classification}
                      </Badge>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 dark:text-foreground">
                          Score Range: {item.range}
                        </p>
                        <p className="text-sm text-slate-600 dark:text-muted-foreground">
                          {item.acceptability}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* File Preview Modal */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-full w-full overflow-auto dark:bg-card">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white dark:bg-card">
              <h3 className="text-lg font-semibold truncate">
                {selectedFile.fileName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFile(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              {selectedFile.fileType?.startsWith("image/") ? (
                <div className="flex justify-center">
                  <OfflineImage
                    fileId={
                      selectedFile?._id ||
                      selectedFile?.id ||
                      selectedFile?._clientId
                    }
                    src={selectedFile.fileUrl}
                    alt={selectedFile.fileName}
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4 dark:text-muted-foreground" />
                  <p className="text-slate-600 dark:text-muted-foreground">
                    Preview not available for this file type
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => window.open(selectedFile.fileUrl, "_blank")}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download File
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Risk Creation Wizard Modal */}
      {showRiskWizard && (
        <div
          onScroll={(e) => e.preventDefault()}
          className="fixed inset-0 bg-black/50 z-[50] flex items-center justify-center p-4"
        >
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b">
              <CardTitle>
                {wizardStep === "select-observation"
                  ? "Step 1: Observation Setup"
                  : "Step 2: Add Risk Details"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 py-6">
              {wizardStep === "select-observation" ? (
                // Step 1: Observation Selection
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 dark:text-muted-foreground">
                    Select an existing observation to add the risk to, or create
                    a new observation with a title.
                  </p>

                  {loadingRiskMatrix ? (
                    <div className="flex justify-center py-8">
                      <LoadingIndicator status="loading" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {existingObservationOptions.length > 1 && (
                        <OptimizedSelect
                          label="Existing Observation"
                          id={"wizard-observation"}
                          value={
                            wizardObservationId || createNewObservationValue
                          }
                          onChange={(value: string) =>
                            setWizardObservationId(
                              value === createNewObservationValue ? "" : value,
                            )
                          }
                          placeholder="Select an existing observation..."
                          options={existingObservationOptions}
                        />
                      )}

                      {shouldShowObservationTitle && (
                        <div>
                          <Label
                            htmlFor="wizard-observation-title"
                            className="text-sm font-medium"
                          >
                            Observation Title
                          </Label>
                          <Input
                            id="wizard-observation-title"
                            placeholder={
                              existingObservationOptions.length > 1
                                ? "Enter a title to create a new observation"
                                : "Enter observation title"
                            }
                            value={wizardObservationTitle}
                            onChange={(e) =>
                              setWizardObservationTitle(e.target.value)
                            }
                            className="mt-2"
                          />
                        </div>
                      )}

                      <p className="text-xs text-slate-500 mt-2 dark:text-muted-foreground">
                        {existingObservationOptions.length > 1
                          ? "Select an existing observation or enter a new title to create a new observation."
                          : "No existing observations were found. Enter a title to create a new observation and add the risk."}
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setShowRiskWizard(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        if (!canProceedToRiskDetails) return;
                        setWizardStep("add-risk");
                      }}
                      disabled={!canProceedToRiskDetails}
                    >
                      Continue to Details
                    </Button>
                  </div>
                </div>
              ) : (
                // Step 2: Risk Details Form
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 dark:text-muted-foreground">
                    Fill in the details for the new risk.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <Label
                        htmlFor="wizard-title"
                        className="text-sm font-medium"
                      >
                        Risk Title
                      </Label>
                      <Input
                        id="wizard-title"
                        placeholder="Brief title for the risk"
                        value={wizardRiskData.title || ""}
                        onChange={(e) =>
                          setWizardRiskData({
                            ...wizardRiskData,
                            title: e.target.value,
                          })
                        }
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label
                        htmlFor="wizard-description"
                        className="text-sm font-medium"
                      >
                        Description
                      </Label>
                      <Textarea
                        id="wizard-description"
                        placeholder="Detailed description of the risk"
                        value={wizardRiskData.description || ""}
                        onChange={(e) =>
                          setWizardRiskData({
                            ...wizardRiskData,
                            description: e.target.value,
                          })
                        }
                        className="mt-2"
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <OptimizedInput
                        label="Severity"
                        id={"wizard-severity"}
                        type="number"
                        min={1}
                        max={5}
                        value={wizardRiskData.severity || 1}
                        onChange={(value: string) => {
                          const parsedValue = Number(value);
                          const clampedValue = Math.min(
                            5,
                            Math.max(1, parsedValue || 1),
                          );
                          setWizardRiskData({
                            ...wizardRiskData,
                            severity: clampedValue as Risk["severity"],
                          });
                        }}
                      />

                      <OptimizedInput
                        label="Likelihood"
                        id={"wizard-likelihood"}
                        type="number"
                        min={1}
                        max={5}
                        value={wizardRiskData.likelihood || 1}
                        onChange={(value: string) => {
                          const parsedValue = Number(value);
                          const clampedValue = Math.min(
                            5,
                            Math.max(1, parsedValue || 1),
                          );
                          setWizardRiskData({
                            ...wizardRiskData,
                            likelihood: clampedValue as Risk["likelihood"],
                          });
                        }}
                      />
                    </div>

                    {/* <div>
                      <Label
                        htmlFor="wizard-recommendation"
                        className="text-sm font-medium"
                      >
                        Recommendation / Mitigation
                      </Label>
                      <Textarea
                        id="wizard-recommendation"
                        placeholder="Recommended actions to mitigate the risk"
                        value={wizardRiskData.recommendation || ""}
                        onChange={(e) =>
                          setWizardRiskData({
                            ...wizardRiskData,
                            recommendation: e.target.value,
                          })
                        }
                        className="mt-2"
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label
                          htmlFor="wizard-assignee"
                          className="text-sm font-medium"
                        >
                          Assignee
                        </Label>
                        <Input
                          id="wizard-assignee"
                          placeholder="Person responsible"
                          value={wizardRiskData.assignee || ""}
                          onChange={(e) =>
                            setWizardRiskData({
                              ...wizardRiskData,
                              assignee: e.target.value,
                            })
                          }
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor="wizard-duedate"
                          className="text-sm font-medium"
                        >
                          Due Date
                        </Label>
                        <Input
                          id="wizard-duedate"
                          type="date"
                          disabled={readOnly}
                          value={wizardRiskData.dueDate || ""}
                          onChange={(e) =>
                            setWizardRiskData({
                              ...wizardRiskData,
                              dueDate: e.target.value,
                            })
                          }
                          className="mt-2"
                        />
                      </div>
                    </div> */}
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setWizardStep("select-observation")}
                    >
                      Back
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowRiskWizard(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={completeRiskWizard}
                      disabled={!wizardRiskData.title}
                    >
                      Create Risk
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Progress Footer */}
      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Risk Stats:
            </span>
            <Badge variant="outline">Total: {riskStats.total}</Badge>
            <Badge className="bg-green-500 text-white">
              Low: {riskStats.low}
            </Badge>
            <Badge className="bg-yellow-500 text-white">
              Medium: {riskStats.medium}
            </Badge>
            <Badge className="bg-red-500 text-white">
              High: {riskStats.high}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress[templateId]?.["key-issue-matrix"] || 0}%
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
